import React, { useState, useEffect } from 'react';

// Types
interface SESTemplate {
    Name: string;
    CreatedTimestamp: string;
}

interface Campaign {
    campaign_id: string;
    name: string;
    description?: string;
    templateName: string;
    segments: string[];
    dailyLimit: number;
    sendRatePerHour?: number;
    scheduledDate: string;
    status: string;
    createdAt: string;
    sentCount?: number;
    targetCount?: number;
    scheduledStartTime?: string;
    scheduledCompletionTime?: string;
    attributeFilters?: any;
    totalContacts?: number | null;
    availableContacts?: number | null;
    availableNote?: string | null;
}

const API_BASE = 'https://xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws';

// State codes constant
const STATE_CODES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'SC', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY', 'MISC_STATES'];

const EmailCampaignDashboard: React.FC = () => {
    // Tab state
    const [activeTab, setActiveTab] = useState<'campaigns' | 'create' | 'templates'>('campaigns');

    // Data state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [templates, setTemplates] = useState<SESTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Segment filter state - single multi-select for all segments
    const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
    const [selectedEngagement, setSelectedEngagement] = useState<string[]>([]);

    // Available segments from database
    const [availableSegments, setAvailableSegments] = useState<Array<{ segment: string, count: number }>>([]);
    const [segmentSearchQuery, setSegmentSearchQuery] = useState('');

    // Live count state
    const [liveCount, setLiveCount] = useState<number | null>(null);
    const [isCountLoading, setIsCountLoading] = useState(false);

    // Campaign form state
    const [campaignName, setCampaignName] = useState('');
    const [campaignDescription, setCampaignDescription] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [excludeDays, setExcludeDays] = useState(3);
    const [sendOption, setSendOption] = useState<'immediate' | 'scheduled'>('immediate');
    const [completionDateTime, setCompletionDateTime] = useState('');

    // Get minimum datetime (2 minutes from now) in user's local time
    const getMinDateTime = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 2);

        // Format for datetime-local input (uses browser's local timezone)
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Clone campaign function
    const cloneCampaign = (campaign: Campaign) => {
        if (!window.confirm(`Clone campaign "${campaign.name}"? This will copy all settings to a new campaign.`)) {
            return;
        }

        // Switch to create tab and populate fields
        setActiveTab('create');
        setCampaignName(`${campaign.name} (Copy)`);
        setCampaignDescription(campaign.description || '');
        setSelectedTemplate(campaign.templateName);
        setSelectedSegments(campaign.segments || []);
        setSelectedEngagement(campaign.attributeFilters?.engagement || []);

        // Reset scheduling
        setSendOption('immediate');
        setCompletionDateTime('');

        alert(`Campaign settings copied! Review and create the new campaign.`);
    };

    // Delete campaign function
    const deleteCampaign = async (campaignId: string, campaignName: string, status: string) => {
        // Only allow deletion of completed, failed, cancelled, or scheduled (not started) campaigns
        const canDelete = ['completed', 'failed', 'cancelled', 'scheduled'].includes(status);

        if (!canDelete) {
            alert('Can only delete campaigns that are completed, failed, cancelled, or not yet started.');
            return;
        }

        if (!window.confirm(`Are you sure you want to DELETE campaign "${campaignName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE}/campaigns/${campaignId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to delete campaign: ${response.status}`);
            }

            alert(`Campaign "${campaignName}" deleted successfully!`);

            // Refresh campaign list
            await loadData();
        } catch (error) {
            console.error('Error deleting campaign:', error);
            alert(`Failed to delete campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Convert Local Time (from datetime-local input) to UTC for backend
    const convertLocalToUTC = (localDateTimeString: string): string => {
        // datetime-local format: "YYYY-MM-DDTHH:mm"
        // The datetime-local input uses the browser's local timezone

        // Simply create a Date object - JavaScript will interpret it as local time
        const localDate = new Date(localDateTimeString);

        // Get the user's timezone name for logging
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localTimeStr = localDate.toLocaleString('en-US', {
            timeZone: userTimezone,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        console.log(`User's timezone: ${userTimezone}`);
        console.log(`Selected local time: ${localTimeStr}`);
        console.log(`Converted to UTC: ${localDate.toISOString()}`);

        return localDate.toISOString();
    };

    // Convert UTC to Local Time for display
    const formatUTCtoLocal = (utcDateString: string): string => {
        if (!utcDateString) return '';

        const date = new Date(utcDateString);
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const localString = date.toLocaleString('en-US', {
            timeZone: userTimezone,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        // Get timezone abbreviation
        const tzAbbr = date.toLocaleTimeString('en-US', {
            timeZone: userTimezone,
            timeZoneName: 'short'
        }).split(' ').pop();

        return `${localString} ${tzAbbr}`;
    };

    // Hardcoded send rate - 14 emails/second for optimal deliverability
    // 14 emails/second = 50,400/hour
    const sendRatePerHour = 50400;

    // Preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewHTML, setPreviewHTML] = useState('');
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    // SES limits
    const SES_DAILY_LIMIT = 107000;
    const SAFE_DAILY_LIMIT = 75000; // 75,000 emails per day
    const [availableQuota, setAvailableQuota] = useState(SAFE_DAILY_LIMIT);

    // Load data on mount
    useEffect(() => {
        loadData();
        loadSegments();
    }, []);

    // Auto-refresh campaigns every 15 seconds if there are running campaigns
    useEffect(() => {
        const hasRunningCampaigns = campaigns.some(c => c.status === 'running' || c.status === 'scheduled');

        if (!hasRunningCampaigns) {
            return;
        }

        const interval = setInterval(() => {
            console.log('Auto-refreshing campaigns...');
            loadData();
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, [campaigns]);

    // Load attribute counts
    const loadSegments = async () => {
        try {
            const token = localStorage.getItem('idToken');
            const response = await fetch(`${API_BASE}/contacts/segments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                console.log('Segments API response:', data);
                const segments = data.segments || [];
                console.log(`Loaded ${segments.length} segments`);
                setAvailableSegments(segments);
            } else {
                console.error('Failed to load segments:', response.status, response.statusText);
                const errorText = await response.text();
                console.error('Error details:', errorText);
                alert(`Failed to load segments: ${response.status} - ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error loading segments:', error);
            alert(`Error loading segments: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    // Auto-update live count when filters change (with debounce)
    useEffect(() => {
        const hasFilters = selectedSegments.length > 0 || selectedEngagement.length > 0;

        if (!hasFilters) {
            setLiveCount(null);
            setIsCountLoading(false);
            return;
        }

        // Debounce: wait 500ms after last change before making API call
        const timeoutId = setTimeout(async () => {
            setIsCountLoading(true);
            try {
                const requestBody: any = {
                    segments: selectedSegments,
                    excludeRecentDays: excludeDays,
                    attributeFilters: {
                        engagement: selectedEngagement
                    }
                };

                console.log('Fetching live count with filters:', requestBody);

                // Add timeout to fetch request (25 seconds - backend has 20 second timeout)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 25000);

                const response = await fetch(`${API_BASE}/contacts/count`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('idToken')}`
                    },
                    body: JSON.stringify(requestBody),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    console.log('Live count response:', data);
                    setLiveCount(data.eligibleContacts || 0);
                } else {
                    const errorText = await response.text();
                    console.error('Failed to fetch live count:', response.status, errorText);
                    setLiveCount(null);
                    // Show error message to user
                    if (response.status === 504 || response.status === 408) {
                        console.error('Count request timed out. Try selecting fewer segments or reducing exclude days.');
                    }
                }
            } catch (error: any) {
                console.error('Error fetching live count:', error);
                if (error.name === 'AbortError') {
                    console.error('Count request timed out after 25 seconds');
                    setLiveCount(null);
                } else {
                    setLiveCount(null);
                }
            } finally {
                setIsCountLoading(false);
            }
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(timeoutId);
        };
    }, [selectedSegments, selectedEngagement, excludeDays]);

    // Transform snake_case API response to camelCase for frontend
    const transformCampaign = (campaign: any): Campaign => ({
        campaign_id: campaign.campaign_id,
        name: campaign.name,
        description: campaign.description,
        templateName: campaign.template_name || campaign.templateName,
        segments: campaign.segments || [],
        dailyLimit: campaign.daily_limit || campaign.dailyLimit || 0,
        sendRatePerHour: campaign.send_rate_per_hour || campaign.sendRatePerHour,
        scheduledDate: campaign.scheduled_date || campaign.scheduledDate || campaign.created_at,
        status: campaign.status,
        createdAt: campaign.created_at || campaign.createdAt,
        sentCount: campaign.sent_count || campaign.sentCount || 0,
        targetCount: campaign.target_count || campaign.targetCount || 0,
        scheduledStartTime: campaign.scheduled_start_time || campaign.scheduledStartTime,
        scheduledCompletionTime: campaign.scheduled_completion_time || campaign.scheduledCompletionTime,
        attributeFilters: campaign.attribute_filters || campaign.attributeFilters,
        totalContacts: campaign.totalContacts || campaign.total_contacts || null,
        availableContacts: campaign.availableContacts || campaign.available_contacts || null,
        availableNote: campaign.availableNote || campaign.available_note || null
    });

    // Load campaigns and templates
    const loadData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('idToken');

            // Load campaigns
            const campaignsRes = await fetch(`${API_BASE}/campaigns`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (campaignsRes.ok) {
                const data = await campaignsRes.json();
                console.log('Campaigns API response:', data);
                const rawCampaigns = data.data || data.campaigns || [];
                const transformedCampaigns = rawCampaigns.map(transformCampaign);
                console.log('Transformed campaigns:', transformedCampaigns.length, transformedCampaigns);
                setCampaigns(transformedCampaigns);
            } else {
                console.error('Failed to load campaigns:', campaignsRes.status, campaignsRes.statusText);
                const errorText = await campaignsRes.text();
                console.error('Campaigns error details:', errorText);
                alert(`Failed to load campaigns: ${campaignsRes.status} - ${campaignsRes.statusText}\n\n${errorText}`);
            }

            // Load templates
            const templatesRes = await fetch(`${API_BASE}/ses/templates`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (templatesRes.ok) {
                const data = await templatesRes.json();
                console.log('Templates API response:', data);
                console.log('Templates array:', data.templates);
                console.log('Number of templates:', data.templates?.length || 0);
                setTemplates(data.templates || []);
            } else {
                console.error('Failed to load templates:', templatesRes.status);
                const errorText = await templatesRes.text();
                console.error('Error details:', errorText);
                alert(`Failed to load templates: ${templatesRes.status} - ${errorText}`);
            }

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Toggle functions
    const toggleEngagement = (engagement: string) => {
        setSelectedEngagement(prev => prev.includes(engagement) ? prev.filter(e => e !== engagement) : [...prev, engagement]);
    };

    // Create campaign
    const createCampaign = async () => {
        const hasFilters = selectedSegments.length > 0 || selectedEngagement.length > 0;

        if (!campaignName || !selectedTemplate) {
            alert('Please fill in campaign name and select a template');
            return;
        }

        if (!hasFilters) {
            alert('Please select at least one segment or engagement filter');
            return;
        }

        // Ensure live count is calculated
        if (liveCount === null || isCountLoading) {
            alert('⏳ Please wait for contact count to finish calculating...');
            return;
        }

        if (liveCount === 0) {
            alert('⚠️ No eligible contacts found with current filters.\n\nTip: Try reducing the "Exclude Recent Sends" days or selecting different filters.');
            return;
        }

        // Always cap at 75K - hard limit
        const targetCount = Math.min(liveCount, SAFE_DAILY_LIMIT);
        const dailyLimit = SAFE_DAILY_LIMIT;

        // Validate scheduled time if scheduling for later
        if (sendOption === 'scheduled') {
            if (!completionDateTime) {
                alert('⚠️ Please select a completion time for the scheduled campaign.');
                return;
            }

            const scheduledTime = new Date(completionDateTime);
            const minTime = new Date();
            minTime.setMinutes(minTime.getMinutes() + 2);

            if (scheduledTime < minTime) {
                alert('⚠️ Scheduled time must be at least 2 minutes from now.');
                return;
            }
        }

        setIsLoading(true);
        try {
            const campaignData: any = {
                name: campaignName,
                description: campaignDescription,
                templateName: selectedTemplate,
                segments: selectedSegments,
                targetCount: targetCount,        // ✅ Use calculated target count
                dailyLimit: dailyLimit,          // ✅ Use calculated daily limit
                excludeRecentDays: excludeDays,
                sendRatePerHour,
                status: 'scheduled',
                attributeFilters: {
                    engagement: selectedEngagement
                }
            };

            // Add scheduled completion time if scheduling for later
            if (sendOption === 'scheduled' && completionDateTime) {
                // Convert Local Time to UTC for backend
                const utcTime = convertLocalToUTC(completionDateTime);
                campaignData.scheduledCompletionTime = utcTime;
            }

            console.log('Creating campaign with data:', campaignData);
            console.log('Current sendOption state:', sendOption);

            const response = await fetch(`${API_BASE}/campaigns`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('idToken')}`
                },
                body: JSON.stringify(campaignData)
            });

            if (response.ok) {
                const result = await response.json();
                const createdCampaignId = result.campaign_id;

                console.log('Campaign created:', result);
                console.log('Send option:', sendOption);
                console.log('Campaign ID:', createdCampaignId);
                console.log('Scheduled completion time:', completionDateTime);

                // If immediate send (and NOT scheduled for later), start the campaign right away
                const shouldStartNow = sendOption === 'immediate' && !completionDateTime && createdCampaignId;

                console.log('Should start now?', shouldStartNow);

                if (shouldStartNow) {
                    console.log('Starting campaign immediately...');
                    try {
                        const startResponse = await fetch(`${API_BASE}/campaigns/${createdCampaignId}/start`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('idToken')}`
                            }
                        });

                        if (startResponse.ok) {
                            const startResult = await startResponse.json();
                            console.log('Campaign started successfully:', startResult);
                        } else {
                            console.error('Failed to start campaign:', response.status, await startResponse.text());
                            alert('⚠️ Campaign created but failed to start. You can start it manually from the campaigns list.');
                        }
                    } catch (error) {
                        console.error('Error starting campaign:', error);
                        alert('⚠️ Campaign created but failed to start. You can start it manually from the campaigns list.');
                    }
                } else {
                    console.log('Not starting immediately - scheduled for:', completionDateTime || 'N/A');
                }

                // Reset form
                setCampaignName('');
                setCampaignDescription('');
                setSelectedTemplate('');
                setSelectedSegments([]);
                setSelectedEngagement([]);

                // Switch to campaigns tab and reload data
                setActiveTab('campaigns');
                await loadData();

                // Show success message after everything loads
                const message = sendOption === 'immediate'
                    ? '✅ Campaign created and started!'
                    : '✅ Campaign created successfully!';
                alert(message);
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(`Failed to create campaign: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error creating campaign:', error);
            alert('Error creating campaign');
        } finally {
            setIsLoading(false);
        }
    };

    // Preview template
    const handlePreviewTemplate = async () => {
        if (!selectedTemplate) return;

        setIsLoadingPreview(true);
        setShowPreview(true);

        try {
            const response = await fetch(`${API_BASE}/ses/templates/${selectedTemplate}/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('idToken')}`
                },
                body: JSON.stringify({
                    templateData: {
                        firstName: 'John',
                        companyName: 'Sample School',
                        unsubscribeUrl: 'https://example.com/unsubscribe'
                    }
                })
            });

            if (response.ok) {
                const data = await response.json();
                setPreviewHTML(data.preview?.htmlBody || '<p>No preview available</p>');
            }
        } catch (error) {
            console.error('Error previewing template:', error);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    // Helper to check if any filters are selected
    const hasAnyFilter = selectedSegments.length > 0 || selectedEngagement.length > 0;

    // Clear all filters
    const clearAllFilters = () => {
        setSelectedSegments([]);
        setSelectedEngagement([]);
    };

    return (
        <div className="email-campaign-dashboard">
            {/* Header */}
            <div className="dashboard-header">
                <div>
                    <h1>📧 Email Campaign Manager</h1>
                    <p className="header-subtitle">Schedule and manage AWS SES email campaigns</p>
                </div>
                <button className="btn-refresh" onClick={loadData} disabled={isLoading}>
                    {isLoading ? '⏳ Loading...' : '🔄 Refresh'}
                </button>
            </div>

            {/* Tabs */}
            <div className="campaign-tabs">
                <button
                    className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
                    onClick={() => setActiveTab('campaigns')}
                >
                    📋 Campaigns
                </button>
                <button
                    className={`tab ${activeTab === 'create' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create')}
                >
                    ➕ Create Campaign
                </button>
                <button
                    className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
                    onClick={() => setActiveTab('templates')}
                >
                    📄 Templates
                </button>
            </div>

            {/* Campaigns List Tab */}
            {activeTab === 'campaigns' && (
                <div className="tab-content">
                    <h2>All Campaigns</h2>
                    {campaigns.length === 0 ? (
                        <div className="empty-state">
                            <p>No campaigns yet. Create your first campaign!</p>
                            <button className="btn-primary" onClick={() => setActiveTab('create')}>
                                Create Campaign
                            </button>
                        </div>
                    ) : (
                        <div className="campaigns-list">
                            {campaigns.map(campaign => {
                                const sentCount = campaign.sentCount || 0;
                                const targetCount = campaign.targetCount || 0;
                                const progress = targetCount > 0 ? Math.floor((sentCount / targetCount) * 100) : 0;
                                const isRunning = campaign.status === 'running';
                                const isBatching = campaign.status === 'batching';

                                return (
                                    <div key={campaign.campaign_id} className="campaign-card">
                                        <div className="campaign-header">
                                            <h3>{campaign.name}</h3>
                                            <span className={`status-badge status-${campaign.status}`}>
                                                {isRunning ? '🔄 Running' : isBatching ? '⏳ Batching' : campaign.status}
                                            </span>
                                        </div>
                                        {campaign.description && (
                                            <p className="campaign-description">{campaign.description}</p>
                                        )}

                                        {/* Progress bar for running campaigns */}
                                        {isRunning && targetCount > 0 && (
                                            <div className="campaign-progress">
                                                <div className="progress-bar">
                                                    <div
                                                        className="progress-fill"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                                <div className="progress-text">
                                                    {sentCount.toLocaleString()} / {targetCount.toLocaleString()} ({progress}%)
                                                </div>
                                            </div>
                                        )}

                                        <div className="campaign-meta">
                                            <div>🆔 Campaign ID: <code style={{fontSize: '0.9em', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px'}}>{campaign.campaign_id}</code></div>
                                            <div>📧 Template: {campaign.templateName}</div>
                                            <div>📊 Sent: {sentCount.toLocaleString()} {targetCount ? `/ ${targetCount.toLocaleString()}` : ''}</div>
                                            {campaign.availableContacts != null && (
                                                <div title={campaign.availableNote || ''}>✅ Available: {campaign.availableContacts.toLocaleString()}
                                                    {campaign.availableNote && (
                                                        <span style={{fontSize: '0.8em', color: '#94a3b8', marginLeft: '6px'}}>({campaign.availableNote})</span>
                                                    )}
                                                </div>
                                            )}
                                            <div>📅 Created: {new Date(campaign.createdAt).toLocaleDateString()}</div>
                                            {campaign.scheduledCompletionTime && (
                                                <div>⏰ Scheduled: {formatUTCtoLocal(campaign.scheduledCompletionTime)}</div>
                                            )}
                                            {campaign.segments && campaign.segments.length > 0 && (
                                                <div>🎯 Segments: {
                                                    campaign.segments.length <= 5
                                                        ? campaign.segments.join(', ')
                                                        : `${campaign.segments.slice(0, 5).join(', ')}... (${campaign.segments.length} total)`
                                                }</div>
                                            )}
                                            <div style={{marginTop: '10px', padding: '10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb'}}>
                                                <div style={{fontSize: '0.85em', color: '#6b7280', marginBottom: '6px'}}>📋 CloudWatch Log Command:</div>
                                                <code style={{
                                                    display: 'block',
                                                    fontSize: '0.8em',
                                                    background: '#1f2937',
                                                    color: '#10b981',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    aws logs tail /aws/lambda/angelwatch-campaign-sender --since 10m --profile angelwatch --format short | grep '[{campaign.campaign_id}]'
                                                </code>
                                                <button
                                                    onClick={() => {
                                                        const command = `aws logs tail /aws/lambda/angelwatch-campaign-sender --since 10m --profile angelwatch --format short | grep '[${campaign.campaign_id}]'`;
                                                        navigator.clipboard.writeText(command).then(() => {
                                                            alert('✅ Command copied to clipboard!');
                                                        }).catch(() => {
                                                            // Fallback for older browsers
                                                            const textArea = document.createElement('textarea');
                                                            textArea.value = command;
                                                            document.body.appendChild(textArea);
                                                            textArea.select();
                                                            document.execCommand('copy');
                                                            document.body.removeChild(textArea);
                                                            alert('✅ Command copied to clipboard!');
                                                        });
                                                    }}
                                                    style={{
                                                        marginTop: '8px',
                                                        padding: '6px 12px',
                                                        fontSize: '0.85em',
                                                        background: '#3b82f6',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    📋 Copy Command
                                                </button>
                                            </div>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="campaign-actions">
                                            <button
                                                className="btn-action btn-clone"
                                                onClick={() => cloneCampaign(campaign)}
                                                title="Clone this campaign"
                                            >
                                                📋 Clone
                                            </button>
                                            {['completed', 'failed', 'cancelled', 'scheduled'].includes(campaign.status) && (
                                                <button
                                                    className="btn-action btn-delete"
                                                    onClick={() => deleteCampaign(campaign.campaign_id, campaign.name, campaign.status)}
                                                    title="Delete this campaign"
                                                    disabled={isLoading}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Create Campaign Tab */}
            {activeTab === 'create' && (
                <div className="tab-content">
                    <h2>Create New Campaign</h2>

                    {/* Section 1: Campaign Details */}
                    <div className="form-section">
                        <h3>1. Campaign Details</h3>
                        <div className="form-group">
                            <label>Campaign Name *</label>
                            <input
                                type="text"
                                value={campaignName}
                                onChange={(e) => setCampaignName(e.target.value)}
                                placeholder="e.g., October Newsletter"
                                className="form-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                value={campaignDescription}
                                onChange={(e) => setCampaignDescription(e.target.value)}
                                placeholder="Optional description..."
                                className="form-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Section 2: Email Template */}
                    <div className="form-section">
                        <h3>2. Email Template</h3>
                        <div className="form-group">
                            <label>Select SES Template *</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'start' }}>
                                <div style={{ flex: 1 }}>
                                    <select
                                        value={selectedTemplate}
                                        onChange={(e) => setSelectedTemplate(e.target.value)}
                                        className="form-select"
                                        disabled={isLoading}
                                    >
                                        <option value="">
                                            {isLoading ? '⏳ Loading templates...' :
                                                templates.length === 0 ? '⚠️ No templates found' :
                                                    '-- Select a template --'}
                                        </option>
                                        {templates.map(template => (
                                            <option key={template.Name} value={template.Name}>
                                                {template.Name}
                                            </option>
                                        ))}
                                    </select>
                                    {!isLoading && templates.length === 0 && (
                                        <div style={{ marginTop: '10px', padding: '10px', background: '#fef3c7', borderRadius: '6px', fontSize: '14px' }}>
                                            ⚠️ No SES templates found. Please check the console for errors or create templates in AWS SES.
                                        </div>
                                    )}
                                    {!isLoading && templates.length > 0 && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                            ✅ {templates.length} template{templates.length !== 1 ? 's' : ''} available
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="btn-secondary"
                                    onClick={loadData}
                                    disabled={isLoading}
                                    style={{ marginTop: 0, whiteSpace: 'nowrap' }}
                                >
                                    🔄 Reload
                                </button>
                            </div>
                        </div>
                        {selectedTemplate && (
                            <button className="btn-secondary" onClick={handlePreviewTemplate}>
                                👁️ Preview Template
                            </button>
                        )}
                    </div>

                    {/* Section 3: Audience Selection */}
                    <div className="form-section">
                        <h3>3. Select Audience by Segments</h3>

                        {/* Live Count Display */}
                        {hasAnyFilter && (
                            <div className="live-count-box">
                                <div className="count-label">📊 Eligible Contacts</div>
                                <div className="count-value">
                                    {isCountLoading ? (
                                        <span>⏳ Calculating... <span style={{ fontSize: '12px', color: '#6b7280' }}>(this may take up to 20 seconds)</span></span>
                                    ) : liveCount !== null ? (
                                        liveCount.toLocaleString()
                                    ) : (
                                        <span style={{ color: '#ef4444' }}>❌ Failed to calculate</span>
                                    )}
                                </div>
                                <div className="count-status">
                                    {liveCount !== null && liveCount <= 75000 ? (
                                        <span>✅ Within daily limit (75,000)</span>
                                    ) : liveCount !== null && liveCount > 75000 ? (
                                        <span>⚠️ Exceeds limit - will take {Math.ceil(liveCount / 75000)} days</span>
                                    ) : liveCount === null && !isCountLoading ? (
                                        <span style={{ color: '#ef4444' }}>⚠️ Unable to calculate count. Try selecting fewer segments or reducing exclude days.</span>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {/* Segments Multi-Select */}
                        <div className="form-group">
                            <label>🏷️ Select Segments {selectedSegments.length > 0 && `(${selectedSegments.length} selected)`}</label>

                            {/* Search Box */}
                            <div style={{ marginBottom: '10px' }}>
                                <input
                                    type="text"
                                    placeholder="🔍 Search segments..."
                                    value={segmentSearchQuery}
                                    onChange={(e) => setSegmentSearchQuery(e.target.value)}
                                    className="form-control"
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        border: '2px solid #ddd',
                                        borderRadius: '6px',
                                        width: '100%'
                                    }}
                                />
                            </div>

                            {/* Quick Select Buttons */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        const stateSegments = availableSegments
                                            .filter(seg => STATE_CODES.includes(seg.segment.toUpperCase()))
                                            .map(seg => seg.segment);
                                        setSelectedSegments(prev => {
                                            // Add all state segments that aren't already selected
                                            const newStates = stateSegments.filter(s => !prev.includes(s));
                                            return [...prev, ...newStates];
                                        });
                                    }}
                                    style={{ fontSize: '14px', padding: '6px 12px' }}
                                >
                                    🗺️ Select All States
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        // Deselect all state segments
                                        setSelectedSegments(prev =>
                                            prev.filter(s => !STATE_CODES.includes(s.toUpperCase()))
                                        );
                                    }}
                                    style={{ fontSize: '14px', padding: '6px 12px' }}
                                >
                                    🗑️ Clear All States
                                </button>
                            </div>

                            <div className="segment-selector">
                                <div className="checkbox-scroll-box" style={{ maxHeight: '400px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                                    {availableSegments.length === 0 ? (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                            {isLoading ? 'Loading segments...' : 'No segments available. Please check the console for errors.'}
                                        </div>
                                    ) : availableSegments
                                        .filter(seg =>
                                            seg.segment.toLowerCase().includes(segmentSearchQuery.toLowerCase())
                                        )
                                        .length === 0 ? (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                            No segments match your search: "{segmentSearchQuery}"
                                        </div>
                                    ) : (
                                        availableSegments
                                            .filter(seg =>
                                                seg.segment.toLowerCase().includes(segmentSearchQuery.toLowerCase())
                                            )
                                            .map(seg => {
                                                const isSelected = selectedSegments.includes(seg.segment);
                                                return (
                                                    <div
                                                        key={seg.segment}
                                                        className={`checkbox-item ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setSelectedSegments(prev => prev.filter(s => s !== seg.segment));
                                                            } else {
                                                                setSelectedSegments(prev => [...prev, seg.segment]);
                                                            }
                                                        }}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <div className="checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                readOnly
                                                            />
                                                            <span style={{ whiteSpace: 'nowrap' }}>{seg.segment}</span>
                                                        </div>
                                                        <span className="checkbox-count">{typeof seg.count === 'string' ? parseInt(seg.count).toLocaleString() : seg.count.toLocaleString()}</span>
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>
                                {selectedSegments.length > 0 && (
                                    <button
                                        className="btn-secondary"
                                        onClick={() => setSelectedSegments([])}
                                        style={{ marginTop: '10px' }}
                                    >
                                        🗑️ Clear All Selections
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Exclude Days */}
                        <div className="form-group">
                            <label>📅 Exclude Recent Sends</label>
                            <select
                                value={excludeDays}
                                onChange={(e) => setExcludeDays(Number(e.target.value))}
                                className="form-select"
                                style={{ maxWidth: '400px' }}
                            >
                                <option value={0}>0 Days - No exclusion</option>
                                <option value={1}>1 Day - Skip if sent in last 1 day</option>
                                <option value={2}>2 Days - Skip if sent in last 2 days</option>
                                <option value={3}>3 Days - Skip if sent in last 3 days</option>
                                <option value={4}>4 Days - Skip if sent in last 4 days</option>
                                <option value={5}>5 Days - Skip if sent in last 5 days</option>
                            </select>
                        </div>

                        {/* Daily Quota Cap - Hard Limit Info */}
                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <div style={{ padding: '15px', backgroundColor: '#f0f9ff', border: '2px solid #0ea5e9', borderRadius: '8px' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
                                    🎯 Daily Campaign Limit: 75,000 emails
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>
                                    All campaigns are automatically capped at 75,000 emails to stay within the daily quota. Contacts will be randomly selected from your eligible pool.
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontStyle: 'italic' }}>
                                    💡 Need to send more? Contact your IT Management to request a daily quota increase.
                                </div>
                                {liveCount !== null && liveCount > SAFE_DAILY_LIMIT && (
                                    <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginTop: '8px', padding: '8px', backgroundColor: '#d1fae5', borderRadius: '4px' }}>
                                        📊 This campaign will send to {SAFE_DAILY_LIMIT.toLocaleString()} of {liveCount.toLocaleString()} eligible contacts
                                    </div>
                                )}
                                {liveCount !== null && liveCount <= SAFE_DAILY_LIMIT && (
                                    <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginTop: '8px' }}>
                                        ✅ All {liveCount.toLocaleString()} eligible contacts will be included
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Filter Summary */}
                        {hasAnyFilter && (
                            <div className="filter-summary">
                                <div className="summary-header">🎯 Active Filters:</div>
                                {selectedSegments.length > 0 && <div>🏷️ Segments: {selectedSegments.join(', ')}</div>}
                                {selectedEngagement.length > 0 && <div>⚡ Engagement: {selectedEngagement.map(e => e.replace('has_', '')).join(', ')}</div>}
                                <button className="btn-secondary" onClick={clearAllFilters}>
                                    🗑️ Clear All Filters
                                </button>
                            </div>
                        )}

                        {/* Tip when no filters */}
                        {!hasAnyFilter && (
                            <div className="info-box">
                                <div style={{ fontSize: '24px', marginBottom: '10px' }}>👆</div>
                                <strong>Select at least one segment above</strong>
                                <div style={{ marginTop: '8px', opacity: 0.9 }}>
                                    Choose segments to see your audience count
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 4: Schedule & Send */}
                    <div className="form-section">
                        <h3>4. Schedule & Send</h3>

                        {/* Send Option */}
                        <div className="send-options">
                            <label onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="radio"
                                    value="immediate"
                                    checked={sendOption === 'immediate'}
                                    onChange={() => {
                                        setSendOption('immediate');
                                        setCompletionDateTime('');
                                    }}
                                />
                                <span>⚡ Send Immediately</span>
                            </label>
                            <label onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="radio"
                                    value="scheduled"
                                    checked={sendOption === 'scheduled'}
                                    onChange={() => setSendOption('scheduled')}
                                />
                                <span>📅 Schedule for Later</span>
                            </label>
                        </div>

                        {sendOption === 'scheduled' && (
                            <div className="form-group" onClick={(e) => e.stopPropagation()}>
                                <label>Desired Completion Time (Your Local Time)</label>
                                <input
                                    type="datetime-local"
                                    value={completionDateTime}
                                    min={getMinDateTime()}
                                    onChange={(e) => setCompletionDateTime(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={() => setSendOption('scheduled')}
                                    className="form-input"
                                    style={{ maxWidth: '300px' }}
                                />
                                <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
                                    ⏰ Minimum: 2 minutes from now
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Create Button */}
                    <div className="form-actions">
                        <button
                            className="btn-primary btn-large"
                            onClick={createCampaign}
                            disabled={isLoading || !campaignName || !selectedTemplate || !hasAnyFilter}
                        >
                            {isLoading ? '⏳ Creating...' : '✅ Create Campaign'}
                        </button>
                    </div>
                </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <div className="tab-content">
                    <h2>Available SES Templates</h2>
                    <div className="templates-grid">
                        {templates.map(template => (
                            <div key={template.Name} className="template-card">
                                <h3>{template.Name}</h3>
                                <p>Created: {new Date(template.CreatedTimestamp).toLocaleString()}</p>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        setSelectedTemplate(template.Name);
                                        handlePreviewTemplate();
                                    }}
                                >
                                    👁️ Preview
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreview && (
                <div className="modal-overlay" onClick={() => setShowPreview(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>📧 Email Template Preview</h2>
                            <button className="close-btn" onClick={() => setShowPreview(false)}>✕</button>
                        </div>
                        <div className="modal-body">
                            {isLoadingPreview ? (
                                <div>⏳ Loading preview...</div>
                            ) : (
                                <div className="email-preview-container" dangerouslySetInnerHTML={{ __html: previewHTML }} />
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowPreview(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles */}
            <style>{`
                .email-campaign-dashboard {
                    padding: 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e5e7eb;
                }

                .header-subtitle {
                    color: #6b7280;
                    margin-top: 5px;
                }

                .btn-refresh {
                    padding: 10px 20px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 600;
                }

                .btn-refresh:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .campaign-tabs {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #e5e7eb;
                }

                .tab {
                    padding: 12px 24px;
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    cursor: pointer;
                    font-weight: 600;
                    color: #6b7280;
                    transition: all 0.2s;
                }

                .tab.active {
                    color: #3b82f6;
                    border-bottom-color: #3b82f6;
                }

                .notification-badge {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    background: #ef4444;
                    color: white;
                    border-radius: 12px;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 700;
                    min-width: 20px;
                    text-align: center;
                }

                .tab-content {
                    padding: 20px 0;
                }

                .form-section {
                    margin-bottom: 30px;
                    padding: 20px;
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                }

                .form-section h3 {
                    margin: 0 0 20px 0;
                    color: #1f2937;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #374151;
                }

                .form-input, .form-textarea, .form-select {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .form-textarea {
                    resize: vertical;
                }

                /* Attribute Selection Styles */
                .attributes-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                    gap: 20px;
                    margin-bottom: 20px;
                }
                
                .attribute-box {
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 15px;
                    background: #fafafa;
                }

                .attribute-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .checkbox-scroll-box {
                    max-height: 200px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    padding: 8px;
                    background: #f9fafb;
                }

                .checkbox-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 10px;
                    margin-bottom: 4px;
                    border-radius: 4px;
                    background: white;
                    border: 1px solid #e5e7eb;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .checkbox-item:hover:not(.disabled) {
                    border-color: #3b82f6;
                    background: #eff6ff;
                }

                .checkbox-item.selected {
                    background: #dbeafe;
                    border-color: #3b82f6;
                }

                .checkbox-item.disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .checkbox-count {
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 600;
                }

                .btn-clear-sm {
                    padding: 4px 12px;
                    background: #f3f4f6;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 12px;
                }

                /* Live Count Box */
                .live-count-box {
                    padding: 25px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px;
                    color: white;
                    margin-bottom: 20px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                }

                .count-label {
                    font-size: 14px;
                    opacity: 0.9;
                    margin-bottom: 8px;
                }

                .count-value {
                    font-size: 48px;
                    font-weight: bold;
                    letter-spacing: 2px;
                }

                .count-status {
                    font-size: 12px;
                    opacity: 0.8;
                    margin-top: 8px;
                }

                /* Filter Summary */
                .filter-summary {
                    padding: 15px;
                    background: #fef3c7;
                    border-radius: 8px;
                    margin-top: 20px;
                }

                .summary-header {
                    font-weight: 600;
                    margin-bottom: 8px;
                }

                .info-box {
                    padding: 20px;
                    background: #eff6ff;
                    border: 2px solid #3b82f6;
                    border-radius: 12px;
                    text-align: center;
                    margin-top: 20px;
                }

                /* Campaign Cards */
                .campaigns-list {
                    display: grid;
                    gap: 20px;
                }

                .campaign-card {
                    padding: 20px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                }

                .campaign-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }

                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-scheduled { background: #fef3c7; color: #92400e; }
                .status-running { background: #dcfce7; color: #166534; }
                .status-paused { background: #fed7aa; color: #9a3412; }
                .status-completed { background: #e0e7ff; color: #3730a3; }
                .status-cancelled { background: #fee2e2; color: #991b1b; }

                .campaign-meta {
                    font-size: 14px;
                    color: #6b7280;
                    margin-bottom: 15px;
                }

                .campaign-actions {
                    display: flex;
                    gap: 10px;
                    padding-top: 15px;
                    border-top: 1px solid #e5e7eb;
                }

                .btn-action {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-clone {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .btn-clone:hover {
                    background: #bfdbfe;
                }

                .btn-delete {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .btn-delete:hover {
                    background: #fecaca;
                }

                .btn-action:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .campaign-meta > div {
                    margin-bottom: 5px;
                }

                /* Campaign Progress */
                .campaign-progress {
                    margin: 15px 0;
                }

                .progress-bar {
                    width: 100%;
                    height: 24px;
                    background: #e5e7eb;
                    border-radius: 12px;
                    overflow: hidden;
                    margin-bottom: 8px;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
                    transition: width 0.5s ease-in-out;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    padding-right: 10px;
                    color: white;
                    font-size: 12px;
                    font-weight: 600;
                }

                .progress-text {
                    font-size: 14px;
                    font-weight: 600;
                    color: #3b82f6;
                    text-align: center;
                }

                /* Templates Grid */
                .templates-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 20px;
                }

                .template-card {
                    padding: 20px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal-content {
                    background: white;
                    border-radius: 12px;
                    max-width: 800px;
                    max-height: 90vh;
                    overflow: auto;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                }

                .modal-header {
                    padding: 20px 30px;
                    border-bottom: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header h2 {
                    margin: 0;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #6b7280;
                }

                .modal-body {
                    padding: 0;
                    background: #f5f5f5;
                    max-height: 70vh;
                    overflow-y: auto;
                }

                .email-preview-container {
                    padding: 20px;
                    background: #f5f5f5;
                    min-height: 400px;
                }

                .email-preview-container table {
                    margin: 0 auto;
                }

                .modal-footer {
                    padding: 20px 30px;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: flex-end;
                }

                /* Buttons */
                .btn-primary {
                    padding: 12px 24px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 600;
                    font-size: 16px;
                }

                .btn-primary:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .btn-primary.btn-large {
                    width: 100%;
                    padding: 16px;
                    font-size: 18px;
                }

                .btn-secondary {
                    padding: 10px 20px;
                    background: white;
                    color: #3b82f6;
                    border: 2px solid #3b82f6;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 600;
                }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    color: #6b7280;
                }

                .form-actions {
                    margin-top: 30px;
                }

                /* Scrollbar styling */
                .checkbox-scroll-box::-webkit-scrollbar {
                    width: 8px;
                }

                .checkbox-scroll-box::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }

                .checkbox-scroll-box::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                }

                .checkbox-scroll-box::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            `}</style>
        </div>
    );
};

export default EmailCampaignDashboard;

